import { useState } from "react";
import { Play } from "lucide-react";
import { useTranslation } from "react-i18next";
import { YouTubeVideo } from "@/lib/socialApi";
import { trackVideoPlay } from "@/lib/analytics";
import { useLanguage } from "@/contexts/LanguageContext";

interface YouTubeVideoCardProps {
    video: YouTubeVideo;
    autoplayOnClick?: boolean;
}

const useFormatDate = () => {
    const { lang } = useLanguage();
    return (iso: string) => {
        try {
            return new Intl.DateTimeFormat(lang, { year: "numeric", month: "long" }).format(
                new Date(iso),
            );
        } catch {
            return "";
        }
    };
};

const YouTubeVideoCard = ({ video, autoplayOnClick = true }: YouTubeVideoCardProps) => {
    const { t } = useTranslation();
    const formatDate = useFormatDate();
    const [isPlaying, setIsPlaying] = useState(false);

    const embedSrc = autoplayOnClick
        ? `${video.embedUrl}?autoplay=1&rel=0`
        : `${video.embedUrl}?rel=0`;

    return (
        <article
            className="group glass-v2 hover-lift-v2 rounded-[24px] overflow-hidden flex flex-col h-full"
            data-cursor="hover"
        >
            <div className="relative aspect-[9/16] bg-black/40 overflow-hidden">
                {isPlaying ? (
                    <iframe
                        src={embedSrc}
                        title={video.title}
                        loading="lazy"
                        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                        allowFullScreen
                        className="absolute inset-0 w-full h-full border-0"
                    />
                ) : (
                    <button
                        type="button"
                        onClick={() => {
                            trackVideoPlay(video.title, video.id);
                            setIsPlaying(true);
                        }}
                        aria-label={t("videos.play_aria", { title: video.title })}
                        className="absolute inset-0 w-full h-full"
                    >
                        <img
                            src={video.thumbnail}
                            alt={video.title}
                            loading="lazy"
                            className="absolute inset-0 w-full h-full object-cover transition-transform duration-[700ms] ease-[cubic-bezier(.23,1,.32,1)] group-hover:scale-[1.06]"
                        />

                        {/* Bottom gradient for title legibility bleed */}
                        <div
                            aria-hidden
                            className="absolute inset-0 bg-gradient-to-t from-background/90 via-background/20 to-transparent"
                        />

                        {/* Corner badge: SHORT */}
                        <span className="absolute top-3 right-3 font-v2-label text-[9px] px-2.5 py-1 rounded-full bg-background/70 backdrop-blur-sm border border-white/10 text-white/80">
                            SHORT
                        </span>

                        {/* Violet→cyan play button */}
                        <div className="absolute inset-0 flex items-center justify-center">
                            <div
                                className="w-16 h-16 rounded-full flex items-center justify-center text-white transition-transform duration-[400ms] ease-[cubic-bezier(.23,1,.32,1)] group-hover:scale-110"
                                style={{
                                    background: "var(--gradient-accent-v2)",
                                    boxShadow: "0 12px 32px -4px hsl(260 100% 55% / 0.6)",
                                }}
                            >
                                <Play size={24} className="ml-0.5" fill="currentColor" />
                            </div>
                        </div>

                        {/* Hover border accent */}
                        <div
                            aria-hidden
                            className="absolute inset-0 rounded-[inherit] opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none"
                            style={{
                                boxShadow: "inset 0 0 0 1px hsl(260 90% 65% / 0.4)",
                            }}
                        />
                    </button>
                )}
            </div>

            <div className="p-5 flex-1 flex flex-col gap-3" dir="rtl">
                <h3 className="font-v2-display text-base md:text-lg text-white leading-snug line-clamp-2 transition-colors duration-300 group-hover:text-white/90">
                    {video.title}
                </h3>
                <p className="font-v2-label text-[10px] text-white/40 mt-auto">
                    {formatDate(video.publishedAt)}
                </p>
            </div>
        </article>
    );
};

export default YouTubeVideoCard;
